/**
 * Ontology Packs 카탈로그 페이지 — `/packs` 라우트.
 *
 * 4층 구조(Block → Pack → Preset → Slice) 의 중간 레이어. 블럭들의 조합체인 "팩" 을
 * 서비스와 무관하게 브라우즈한다. 블럭 페이지와 동일한 패턴:
 *
 * - 상단 stats — 총 팩 개수 / 그룹 수 / 사용 중 블럭 수 / 프리셋 수
 * - 그룹 필터 탭 (foundation / build / strategy / gtm / craft / cross-cutting / all)
 * - 검색: displayName · description · 포함 블럭 id 로 필터
 * - 그리드 카드 — 아이콘 · 이름 · 설명 · 포함 블럭 chip (/blocks#block-id 앵커)
 */
import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Term } from "@/shared/components/tooltip/Term.tsx"
import { AdminClient, type OntologyBlock, type OntologyPack, type PackGroup } from "../api/admin-client.ts"
import type { AuthContext } from "../components/AdminAuthGate.tsx"
import { PackPreviewModal } from "../components/ontology/PackPreviewModal.tsx"
import { Skeleton, SkeletonText } from "../components/Skeleton.tsx"
import { useInfiniteScroll } from "../hooks/useInfiniteScroll.ts"

/** Pack 카드 스켈레톤 — icon + title + desc 2줄 + stats + block chips */
function PackCardSkeleton() {
	return (
		<div className="skel-card" aria-hidden>
			<div className="skel-card__head">
				<Skeleton width={32} height={32} radius={8} />
				<div className="skel-card__title-group">
					<Skeleton width="55%" height={14} />
					<Skeleton width="35%" height={11} />
				</div>
				<Skeleton width={68} height={18} radius={999} />
			</div>
			<SkeletonText lines={2} lineHeight={11} />
			<div style={{ display: "flex", gap: 10 }}>
				<Skeleton width={60} height={11} />
				<Skeleton width={55} height={11} />
				<Skeleton width={70} height={11} />
			</div>
			<div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
				<Skeleton width={90} height={20} radius={999} />
				<Skeleton width={110} height={20} radius={999} />
				<Skeleton width={80} height={20} radius={999} />
				<Skeleton width={100} height={20} radius={999} />
			</div>
		</div>
	)
}

type GroupFilter = "all" | PackGroup

const GROUP_TABS: ReadonlyArray<{ readonly key: GroupFilter; readonly label: string }> = [
	{ key: "all", label: "전체" },
	{ key: "foundation", label: "기반" },
	{ key: "build", label: "개발" },
	{ key: "strategy", label: "전략" },
	{ key: "gtm", label: "GTM" },
	{ key: "craft", label: "기획" },
	{ key: "cross-cutting", label: "범분야" }
]

export function PacksPage(props: { readonly auth: AuthContext }) {
	const [packs, setPacks] = useState<ReadonlyArray<OntologyPack>>([])
	const [blocks, setBlocks] = useState<ReadonlyArray<OntologyBlock>>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | undefined>()
	const [group, setGroup] = useState<GroupFilter>("all")
	const [query, setQuery] = useState("")
	const [previewPack, setPreviewPack] = useState<OntologyPack | undefined>()
	// `?pack=<id>` 공유 URL — 카드 클릭 → URL 반영 / 외부 링크 → 모달 자동 오픈
	const [searchParams, setSearchParams] = useSearchParams()

	const client = useMemo(
		() => new AdminClient(props.auth.baseUrl, props.auth.invalidateSession),
		[props.auth.baseUrl, props.auth.invalidateSession]
	)

	useEffect(() => {
		let cancelled = false
		setLoading(true)
		void (async () => {
			try {
				const [packList, blockList] = await Promise.all([
					client.getOntologyPacks(),
					client.getOntologyBlocks()
				])
				if (!cancelled) {
					setPacks(packList)
					setBlocks(blockList)
				}
			} catch (e) {
				if (!cancelled) setError((e as Error).message)
			} finally {
				if (!cancelled) setLoading(false)
			}
		})()
		return () => {
			cancelled = true
		}
	}, [client])

	const blockById = useMemo(() => new Map(blocks.map((b) => [b.id, b])), [blocks])
	const packById = useMemo(() => new Map(packs.map((p) => [p.id, p])), [packs])

	// URL `?pack=<id>` → previewPack 동기화
	useEffect(() => {
		const id = searchParams.get("pack")
		if (id) {
			const p = packById.get(id)
			if (p && p.id !== previewPack?.id) setPreviewPack(p)
		} else if (previewPack) {
			setPreviewPack(undefined)
		}
	}, [searchParams, packById, previewPack])

	const openPreview = (p: OntologyPack) => {
		setPreviewPack(p)
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev)
				next.set("pack", p.id)
				return next
			},
			{ replace: false }
		)
	}

	const closePreview = () => {
		setPreviewPack(undefined)
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev)
				next.delete("pack")
				return next
			},
			{ replace: true }
		)
	}

	const byGroup = useMemo(() => {
		const map = new Map<PackGroup, OntologyPack[]>()
		for (const p of packs) {
			const arr = map.get(p.group) ?? []
			arr.push(p)
			map.set(p.group, arr)
		}
		return map
	}, [packs])

	const filtered = useMemo((): ReadonlyArray<OntologyPack> => {
		const pool = group === "all" ? packs : (byGroup.get(group) ?? [])
		const q = query.trim().toLowerCase()
		if (!q) return pool
		return pool.filter((p) => {
			if (p.displayName.toLowerCase().includes(q)) return true
			if (p.description.toLowerCase().includes(q)) return true
			for (const id of p.blockIds) {
				if (id.toLowerCase().includes(q)) return true
				const b = blockById.get(id)
				if (b?.displayName.toLowerCase().includes(q)) return true
			}
			return false
		})
	}, [packs, byGroup, group, query, blockById])

	const filterActive = query.trim().length > 0 || group !== "all"
	const usedBlockIds = useMemo(() => new Set(packs.flatMap((p) => p.blockIds)), [packs])
	const {
		visible: paged,
		sentinelRef,
		hasMore,
		loadedCount,
		totalCount
	} = useInfiniteScroll(filtered, {
		pageSize: 20
	})

	return (
		<div className="content">
			<div className="page-head">
				<div>
					<h2>
						온톨로지 <Term name="pack">팩</Term>
					</h2>
					<div className="sub">
						<Term name="block">블럭</Term> 의 조합체. 한 <Term name="pack">팩</Term> 은 여러 블럭을 묶어
						도메인별 재사용 단위를 만듭니다.
					</div>
				</div>
			</div>

			{error && (
				<div className="alert alert-danger" style={{ marginBottom: 18 }}>
					{error}
				</div>
			)}

			<div className="stats">
				<div className="stat">
					<div className="stat-v">
						{loading ? <Skeleton width={48} height={22} radius={6} inline /> : packs.length}
					</div>
					<div className="stat-k">총 팩 수</div>
				</div>
				<div className="stat">
					<div className="stat-v">
						{loading ? <Skeleton width={48} height={22} radius={6} inline /> : byGroup.size}
					</div>
					<div className="stat-k">그룹 수</div>
				</div>
				<div className="stat">
					<div className="stat-v">
						{loading ? <Skeleton width={48} height={22} radius={6} inline /> : usedBlockIds.size}
					</div>
					<div className="stat-k">사용 중인 블럭 수</div>
				</div>
				<div className="stat">
					<div className="stat-v">
						{loading
							? "—"
							: (packs.reduce((sum, p) => sum + p.blockIds.length, 0) / Math.max(packs.length, 1)).toFixed(1)}
					</div>
					<div className="stat-k">팩당 평균 블럭</div>
				</div>
			</div>

			<div className="blocks-page-toolbar">
				<label className="table-search">
					<svg
						className="icon"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<title>검색</title>
						<circle cx="11" cy="11" r="7" />
						<path d="m21 21-4.3-4.3" />
					</svg>
					<input
						type="search"
						placeholder="팩 이름 · 설명 · 포함 블럭 검색"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						spellCheck={false}
					/>
				</label>
				<div className="blocks-page-tabs" role="tablist" aria-label="그룹 필터">
					{GROUP_TABS.map((tab) => {
						const isActive = group === tab.key
						const count = tab.key === "all" ? packs.length : (byGroup.get(tab.key)?.length ?? 0)
						return (
							<button
								key={tab.key}
								type="button"
								role="tab"
								aria-selected={isActive}
								className={`blocks-page-tab${isActive ? " is-active" : ""}`}
								onClick={() => setGroup(tab.key)}
							>
								{tab.label}
								<span className="blocks-page-tab-count">{count}</span>
							</button>
						)
					})}
				</div>
			</div>

			{loading ? (
				<div className="blocks-page-grid">
					{Array.from({ length: 6 }, (_, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: deterministic skeleton
						<PackCardSkeleton key={i} />
					))}
				</div>
			) : filtered.length === 0 ? (
				<div className="table-empty">
					<p>조건에 맞는 팩이 없습니다.</p>
					{filterActive && (
						<button
							type="button"
							className="btn btn-sm btn-ghost"
							onClick={() => {
								setQuery("")
								setGroup("all")
							}}
						>
							필터 초기화
						</button>
					)}
				</div>
			) : (
				<>
					<div className="blocks-page-grid">
						{paged.map((pack) => (
							<PackCard key={pack.id} pack={pack} blockById={blockById} onOpenPreview={openPreview} />
						))}
					</div>
					{totalCount > 0 && (
						<div ref={sentinelRef} className="infinite-scroll-sentinel" role="status" aria-live="polite">
							{hasMore ? `불러오는 중… (${loadedCount} / ${totalCount})` : `전체 ${totalCount}개 표시됨`}
						</div>
					)}
				</>
			)}

			{previewPack && <PackPreviewModal pack={previewPack} allBlocks={blocks} onClose={closePreview} />}
		</div>
	)
}

function PackCard(props: {
	readonly pack: OntologyPack
	readonly blockById: ReadonlyMap<string, OntologyBlock>
	readonly onOpenPreview: (p: OntologyPack) => void
}) {
	const { pack, blockById, onOpenPreview } = props
	return (
		// biome-ignore lint/a11y/useSemanticElements: 카드 레이아웃 유지 위해 article + role="button"
		<article
			id={pack.id}
			className="blocks-page-card blocks-page-card--interactive"
			// biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: 상동
			role="button"
			tabIndex={0}
			onClick={() => onOpenPreview(pack)}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault()
					onOpenPreview(pack)
				}
			}}
			aria-label={`${pack.displayName} 팩 상세 보기`}
		>
			<header className="blocks-page-card-head">
				<span className="blocks-page-card-icon" aria-hidden>
					{pack.icon}
				</span>
				<div className="blocks-page-card-title">
					<strong>{pack.displayName}</strong>
					<code className="inline">{pack.id}</code>
				</div>
				<span className={`badge blocks-page-card-cat pack-group-${pack.group}`}>{pack.group}</span>
			</header>

			<p className="blocks-page-card-desc">{pack.description}</p>

			<div className="blocks-page-card-stats">
				<span>블럭 {pack.blockIds.length}</span>
				<span>종류 {pack.slice.kinds.length}</span>
				<span>관계 {pack.slice.relations.length}</span>
				<span>형태 {pack.slice.shapes.length}</span>
			</div>

			<div className="blocks-page-card-packs">
				<span className="blocks-page-card-packs-label">블럭</span>
				{pack.blockIds.length === 0 ? (
					<span className="muted" style={{ fontSize: 11 }}>
						블럭 없음
					</span>
				) : (
					pack.blockIds.map((id) => {
						const b = blockById.get(id)
						return (
							<a
								key={id}
								href={`/blocks?block=${encodeURIComponent(id)}`}
								className="chip blocks-page-card-pack"
								title={b?.description ?? id}
								onClick={(e) => e.stopPropagation()}
							>
								{b ? `${b.icon} ${b.displayName}` : id}
							</a>
						)
					})
				)}
			</div>
		</article>
	)
}
